import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { IoPerson } from "react-icons/io5";
import { BsFillPersonLinesFill } from "react-icons/bs";
import { MdLocationPin } from "react-icons/md";
import { IoMdSettings } from "react-icons/io";
import { BsBank2 } from "react-icons/bs";
import { PiSirenBold } from "react-icons/pi";
import Cropper from 'react-easy-crop';
import DatePickerLib from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// ─── Constants ────────────────────────────────────────────────
const ROLES          = { EMPLOYEE: 'EMPLOYEE', MANAGER: 'MANAGER' };
const GENDER         = { MALE: 'MALE', FEMALE: 'FEMALE', OTHER: 'OTHER' };
const BLOOD_GROUPS   = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const MARITAL_STATUS = ['Single', 'Married', 'Divorced', 'Widowed'];
const LOCATIONS      = ['Bengaluru', 'Mangaluru'];

// ─── Helpers ──────────────────────────────────────────────────
const toDDMMYYYY = (val) => {
  if (!val) return '';
  const [y, m, d] = val.split('-');
  if (!y || !m || !d) return val;
  return `${d}-${m}-${y}`;
};

const calcAge = (dob) => {
  if (!dob) return '';
  const today = new Date();
  const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const mo = today.getMonth() - birth.getMonth();
  if (mo < 0 || (mo === 0 && today.getDate() < birth.getDate())) age--;
  return age >= 0 ? age : '';
};

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getCroppedImg = async (imageSrc, cropPixels) => {
  const image = await createImage(imageSrc);

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // Max output dimensions
  const MAX_SIZE = 700;

  const scale = Math.min(
    MAX_SIZE / cropPixels.width,
    MAX_SIZE / cropPixels.height,
    1
  );

  canvas.width = cropPixels.width * scale;
  canvas.height = cropPixels.height * scale;

  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    canvas.width,
    canvas.height
  );

  // Compress dynamically until < 1 MB
  return new Promise((resolve) => {

    let quality = 0.8;

    const generateBlob = () => {
      canvas.toBlob(
        (blob) => {

          if (!blob) return;

          // If image still > 1MB reduce quality further
          if (blob.size > 1024 * 1024 && quality > 0.3) {
            quality -= 0.1;
            generateBlob();
            return;
          }

          resolve(blob);
        },
        'image/jpeg',
        quality
      );
    };

    generateBlob();
  });
};


// ─── Shared UI primitives ─────────────────────────────────────
const SectionHeader = ({ icon, title, color = 'from-slate-700 to-slate-900' }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className={`flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br ${color} text-white text-sm shadow-md`}>
      {icon}
    </div>
    <h3 className="text-sm font-bold uppercase tracking-widest text-slate-600">{title}</h3>
    <div className="flex-1 h-px bg-gradient-to-r from-slate-200 to-transparent ml-2" />
  </div>
);

const Field = ({ label, required, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider pl-1">
      {label}{required && <span className="text-rose-400 normal-case tracking-normal ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const inputCls = `w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50/60
  text-slate-800 text-sm placeholder-slate-300
  focus:outline-none focus:ring-2 focus:ring-slate-400/40 focus:border-slate-400 focus:bg-white
  transition-all duration-200`;

const TextInput = ({ value, onChange, type = 'text', placeholder = '', className, ...rest }) => (
  <input
    type={type}
    value={value ?? ''}
    onChange={onChange}
    placeholder={placeholder}
    className={className ?? inputCls}
    {...rest}
  />
);

const SelectInput = ({ value, onChange, children }) => (
  <div className="relative">
    <select value={value ?? ''} onChange={onChange} className={`${inputCls} cursor-pointer appearance-none`}>
      {children}
    </select>
    <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
      fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  </div>
);

const TextArea = ({ value, onChange, placeholder = '', rows = 3 }) => (
  <textarea
    value={value ?? ''}
    onChange={onChange}
    placeholder={placeholder}
    rows={rows}
    className={`w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50/60
      text-slate-800 text-sm placeholder-slate-300 resize-none
      focus:outline-none focus:ring-2 focus:ring-slate-400/40 focus:border-slate-400 focus:bg-white
      transition-all duration-200`}
  />
);

const DatePicker = ({
  value,
  onChange,
  placeholder = 'DD-MM-YYYY',
}) => {
  return (
    <DatePickerLib
      selected={value ? new Date(value) : null}
      onChange={(date) => {
        if (!date) {
          onChange({ target: { value: '' } });
          return;
        }

        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');

        onChange({
          target: {
            value: `${yyyy}-${mm}-${dd}`,
          },
        });
      }}
      dateFormat="dd-MM-yyyy"
      placeholderText={placeholder}
      showMonthDropdown
      showYearDropdown
      dropdownMode="select"
      yearDropdownItemNumber={100}
      scrollableYearDropdown
      maxDate={new Date()}
      className={`${inputCls} pr-10`}
      wrapperClassName="w-full"
    />
  );
};

// ─── Profile Picture Uploader ─────────────────────────────────
const ProfilePictureUploader = ({ previewUrl, onUploadComplete }) => {
  const fileInputRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropComplete = (_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  };

  const handleFileChange = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    if (!file.type?.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }


    const reader = new FileReader();

    reader.onload = () => {
      setImageSrc(reader.result);
    };

    reader.readAsDataURL(file);
  };

  const uploadCroppedImage = async () => {
    try {
      setUploading(true);

      const croppedBlob = await getCroppedImg(
        imageSrc,
        croppedAreaPixels
      );

      const formData = new FormData();
      formData.append(
        'file',
        new File([croppedBlob], 'profile.jpg', {
          type: 'image/jpeg',
        })
      );

      const res = await api.post('/api/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          setProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });

      const fileName = res?.data;

      const imageUrl =
        `${process.env.REACT_APP_API_URL}/uploads/${fileName}`;

      onUploadComplete?.(imageUrl);

      toast.success('Profile picture updated');

      setImageSrc(null);
      setZoom(1);
    } catch (err) {
      console.error(err);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div className="flex flex-col items-center gap-4">

      {/* Preview */}
      <div
        onClick={() => !uploading && fileInputRef.current?.click()}
        className="relative w-28 h-28 rounded-full border-2 border-dashed border-slate-300 
        bg-slate-50 flex items-center justify-center overflow-hidden
        hover:border-indigo-400 hover:bg-indigo-50/30 transition-all duration-200 cursor-pointer group"
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Profile"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="text-slate-400 text-xs">
            Upload
          </div>
        )}

{uploading && (
  <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px] flex flex-col items-center justify-center gap-3">

    <div className="relative w-14 h-14">
      <div className="absolute inset-0 rounded-full border-4 border-white/20" />

      <div
        className="absolute inset-0 rounded-full border-4 border-transparent border-t-white animate-spin"
      />
    </div>

    <div className="w-28 h-2 bg-white/20 rounded-full overflow-hidden">
      <div
        className="h-full bg-white rounded-full transition-all duration-300"
        style={{ width: `${progress}%` }}
      />
    </div>

    <p className="text-white text-xs font-semibold tracking-wide">
      Uploading {progress}%
    </p>
  </div>
)}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="text-xs font-semibold text-indigo-600 underline"
      >
        {previewUrl ? 'Change Photo' : 'Upload Photo'}
      </button>

      {/* Crop Modal */}
      {imageSrc && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-xl p-5 shadow-2xl">

            <h2 className="text-lg font-bold text-slate-800 mb-4">
              Adjust Profile Photo
            </h2>

            <div className="relative w-full h-[400px] bg-slate-100 rounded-xl overflow-hidden">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="rect"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>

            <div className="mt-5">
              <label className="text-sm text-slate-600">
                Zoom
              </label>

              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(e.target.value)}
                className="w-full"
              />
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setImageSrc(null)}
                className="px-4 py-2 rounded-xl border border-slate-300 text-slate-700"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={uploadCroppedImage}
                className="px-5 py-2 rounded-xl bg-slate-900 text-white"
              >
                Save Photo
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────
function AddEmployee() {
  const navigate = useNavigate();

  const [employeeData, setEmployeeData] = useState({
    role:                   ROLES.EMPLOYEE,
    office:                 'SPC',
    gender:                 GENDER.MALE,
    emergencyContactLocal:  '',
    emergencyContactFamily: '',
  });
  const [managers,      setManagers]      = useState([]);
  const [profilePicUrl, setProfilePicUrl] = useState('');

  const [departments,   setDepartments]   = useState([]);
  const [customDept,    setCustomDept]    = useState(false);

  const set     = (key) => (e)  => setEmployeeData((prev) => ({ ...prev, [key]: e.target.value }));
  const setDate = (key) => (e)  => setEmployeeData((prev) => ({ ...prev, [key]: e.target.value }));

  useEffect(() => {
    api.get('/auth/managers')
      .then((res) => setManagers(res.data))
      .catch((err) => console.error('Failed to fetch managers', err));

    api.get('/api/departments/all')
      .then((res) => {
        setDepartments(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => console.error('Failed to fetch departments', err));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...employeeData,
      profilePictureUrl: profilePicUrl,
    };
    try {
      const response = await api.post('/auth/employee', payload);
      if (response.status === 201 || response.status === 200) {
        toast.success('Employee added successfully');
        navigate(-1);
      }
    } catch (error) {
      console.error('Error adding employee:', error);
      toast.error(error?.response?.data?.message || 'Failed to add employee');
    }
  };

  const age = calcAge(employeeData.dateOfBirth);

  return (
    <div className="min-h-screen bg-slate-100 px-4 sm:px-8 py-8">

      {/* ── Page Header ── */}
      <div className="w-full mb-6 flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-1">Human Resources</p>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight">On Boarding employee</h1>
        </div>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors
            px-4 py-2 rounded-xl hover:bg-white border border-transparent hover:border-slate-200"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="w-full space-y-5">

        {/* ── 1. Basic Information ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <SectionHeader icon={<IoPerson />} title="Basic Information" color="from-indigo-500 to-indigo-700" />
          <div className="flex flex-col sm:flex-row gap-8 items-start">
            <div className="flex-shrink-0">
              <ProfilePictureUploader
                previewUrl={profilePicUrl}
                onUploadComplete={(url) => setProfilePicUrl(url)}
              />
            </div>
            <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
              <Field label="Employee ID" required>
                <TextInput value={employeeData.employeeId} onChange={set('employeeId')} placeholder="EMP001" required />
              </Field>
              <Field label="Full Name" required>
                <TextInput value={employeeData.name} onChange={set('name')} placeholder="John Doe" required />
              </Field>
              <Field label="Nick Name" required>
                <TextInput value={employeeData.shortName} onChange={set('shortName')} placeholder="Johnny" />
              </Field>
              <Field label="Date of Joining">
                <DatePicker value={employeeData.dateOfJoining} onChange={setDate('dateOfJoining')} />
              </Field>
              <Field label="Designation">
                <TextInput value={employeeData.designation} onChange={set('designation')} placeholder="Software Engineer" />
              </Field>
              <Field label="Department">
                {!customDept && departments.length > 0 ? (
                  <SelectInput
                    value={employeeData.department || ''}
                    onChange={(e) => {
                      if (e.target.value === '__custom__') {
                        setCustomDept(true);
                        setEmployeeData((prev) => ({ ...prev, department: '' }));
                      } else {
                        set('department')(e);
                      }
                    }}
                  >
                    <option value="">Select Department</option>
                    {departments.map((d) => (
                      <option key={d.id || d.departmentName} value={d.departmentName}>
                        {d.departmentName}
                      </option>
                    ))}
                    {employeeData.department && !departments.some((d) => d.departmentName === employeeData.department) && (
                      <option value={employeeData.department}>{employeeData.department}</option>
                    )}
                    <option value="__custom__">+ Add Custom Department</option>
                  </SelectInput>
                ) : (
                  <div className="flex gap-2 items-center">
                    <TextInput
                      value={employeeData.department || ''}
                      onChange={set('department')}
                      placeholder="e.g. Engineering, Sales..."
                    />
                    {departments.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setCustomDept(false)}
                        className="px-3 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-xl whitespace-nowrap transition-colors"
                        title="Choose from list"
                      >
                        List
                      </button>
                    )}
                  </div>
                )}
              </Field>
              <Field label="Location">
                <SelectInput value={employeeData.location} onChange={set('location')}>
                  <option value="">Select</option>
                  {LOCATIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </SelectInput>
              </Field>
              <Field label="Date of Birth">
                <DatePicker value={employeeData.dateOfBirth} onChange={setDate('dateOfBirth')} />
              </Field>
              <Field label="Age">
                <TextInput
                  value={age !== '' ? `${age} years` : ''}
                  placeholder="Auto-calculated from DOB"
                  readOnly
                  className={`${inputCls} bg-slate-100 text-slate-500 cursor-default`}
                />
              </Field>
              <Field label="Gender">
                <SelectInput value={employeeData.gender} onChange={set('gender')}>
                  {Object.values(GENDER).map((g) => <option key={g} value={g}>{g}</option>)}
                </SelectInput>
              </Field>
            </div>
          </div>
        </div>

        {/* ── 2. Personal Details ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <SectionHeader icon={<BsFillPersonLinesFill />} title="Personal Details" color="from-violet-500 to-violet-700" />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
            <Field label="Email" required>
              <TextInput type="email" value={employeeData.email} onChange={set('email')} placeholder="john@company.com" required />
            </Field>
            <Field label="Phone Number" required>
              <TextInput type="tel" value={employeeData.contactNumber} onChange={set('contactNumber')} placeholder="+91 9876543210" required />
            </Field>
            <Field label="Blood Group">
              <SelectInput value={employeeData.bloodGroup} onChange={set('bloodGroup')}>
                <option value="">Select</option>
                {BLOOD_GROUPS.map((b) => <option key={b} value={b}>{b}</option>)}
              </SelectInput>
            </Field>
            <Field label="Marital Status">
              <SelectInput value={employeeData.maritalStatus} onChange={set('maritalStatus')}>
                <option value="">Select</option>
                {MARITAL_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
              </SelectInput>
            </Field>
            <Field label="Father's Name">
              <TextInput value={employeeData.fatherName} onChange={set('fatherName')} placeholder="Father's full name" />
            </Field>
            <Field label="Mother's Name">
              <TextInput value={employeeData.motherName} onChange={set('motherName')} placeholder="Mother's full name" />
            </Field>
            <Field label="Spouse Name">
              <TextInput value={employeeData.spouseName} onChange={set('spouseName')} placeholder="Spouse's full name" />
            </Field>
            <Field label="Religion">
              <TextInput value={employeeData.religion} onChange={set('religion')} placeholder="Religion" />
            </Field>
            <Field label="Medical History">
              <TextInput value={employeeData.medicalHistory} onChange={set('medicalHistory')} placeholder="None / describe" />
            </Field>
            <Field label="Identification Mark">
              <TextInput value={employeeData.personalIdentificationMarks} onChange={set('personalIdentificationMarks')} placeholder="e.g. Mole on left cheek" />
            </Field>
          </div>
        </div>

        {/* ── 3. Address Details ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <SectionHeader icon={<MdLocationPin />} title="Address Details" color="from-emerald-500 to-emerald-700" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Field label="Present Address">
              <TextArea value={employeeData.presentAddress} onChange={set('presentAddress')}
                placeholder="Door No, Street, City, State, PIN" rows={4} />
            </Field>
            <Field label="Permanent Address">
              <TextArea value={employeeData.permanentAddress} onChange={set('permanentAddress')}
                placeholder="Door No, Street, City, State, PIN" rows={4} />
            </Field>
          </div>
        </div>

        {/* ── 4. Emergency Contacts ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <SectionHeader icon={<PiSirenBold />} title="Emergency Contacts" color="from-rose-500 to-rose-700" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-3">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 pl-1">Local Contact</p>
              <div className="flex flex-col gap-3 p-4 rounded-xl bg-slate-50/50 border border-slate-100">
                <Field label="Phone Number">
                  <TextInput
                    type="tel"
                    value={employeeData.emergencyContactLocal}
                    onChange={set('emergencyContactLocal')}
                    placeholder="+91 9876543210"
                  />
                </Field>
              </div>
            </div>
            <div className="space-y-3">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 pl-1">Immediate Family Contact</p>
              <div className="flex flex-col gap-3 p-4 rounded-xl bg-slate-50/50 border border-slate-100">
                <Field label="Phone Number">
                  <TextInput
                    type="tel"
                    value={employeeData.emergencyContactFamily}
                    onChange={set('emergencyContactFamily')}
                    placeholder="+91 9876543210"
                  />
                </Field>
              </div>
            </div>
          </div>
        </div>

        {/* ── 5. Bank & Statutory Details ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <SectionHeader icon={<BsBank2 />} title="Bank & Statutory Details" color="from-amber-500 to-amber-700" />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
            <Field label="Bank Name & Branch">
              <TextInput value={employeeData.bankName} onChange={set('bankName')} placeholder="HDFC Bank, MG Road" />
            </Field>
            <Field label="IFSC Code">
              <TextInput value={employeeData.ifsccode} onChange={set('ifsccode')} placeholder="HDFC0001234" />
            </Field>
            <Field label="Account Number">
              <TextInput value={employeeData.accountNumber} onChange={set('accountNumber')} placeholder="1234567890" />
            </Field>
            <Field label="Aadhar Number">
              <TextInput value={employeeData.aadarNo} onChange={set('aadarNo')}
                placeholder="XXXX XXXX XXXX" pattern="[0-9]{12}" maxLength={12} />
            </Field>
            <Field label="PAN Number">
              <TextInput value={employeeData.panno} onChange={set('panno')} placeholder="ABCDE1234F" />
            </Field>
            <Field label="UAN Number">
              <TextInput value={employeeData.uanno} onChange={set('uanno')} placeholder="100XXXXXXXXX" />
            </Field>
          </div>
        </div>

        {/* ── 6. Role & Assignment ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <SectionHeader icon={<IoMdSettings />} title="Role & Assignment" color="from-slate-600 to-slate-800" />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4 items-end">
            <Field label="Role">
              <SelectInput value={employeeData.role} onChange={set('role')}>
                {Object.values(ROLES).map((r) => <option key={r} value={r}>{r}</option>)}
              </SelectInput>
            </Field>
            <Field label="Reporting Manager">
              <SelectInput value={employeeData.reportingManager} onChange={set('reportingManager')}>
                <option value="">None</option>
                {managers.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
              </SelectInput>
            </Field>
            <div className="flex items-end">
              <button
                type="submit"
                className="w-full py-3 px-6 rounded-xl bg-gradient-to-r from-slate-800 to-slate-900
                  text-white text-sm font-bold uppercase tracking-widest
                  hover:from-slate-700 hover:to-slate-800 active:scale-95
                  transition-all duration-200 shadow-lg hover:shadow-xl
                  flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
                Save Employee
              </button>
            </div>
          </div>
        </div>

      </form>
    </div>
  );
}

export default AddEmployee;